/**
 * This file contains the types for the taught courses API which can be used to fetch courses that an instructor has taught
 *
 * This API is exposed at: /api-2.0/users/31334738/taught-courses/?page=1&page_size=3&organizationCoursesOnly=false&filter_hq_courses=true&fields[user]=@all
 *
 * It is used to fetch courses for the "More from instructor" section on the course page as well as the instructor details.
 *
 */

/**
 * Field selection options for taught courses
 */
export enum CourseFieldSelection {
    ID = 'id',
    TITLE = 'title',
    URL = 'url',
    HEADLINE = 'headline',
    IMAGE = 'image_100x100',
    INSTRUCTIONAL_LEVEL = 'instructional_level_simple',
    AVG_RATING = 'avg_rating',
    NUM_REVIEWS = 'num_reviews',
    NUM_LECTURES = 'num_published_lectures',
    CAPTION_LANGUAGES = 'caption_languages',
    LOCALE = 'locale',
    PUBLISHED_TITLE = 'published_title',
    IS_PRACTICE_TEST_COURSE = 'is_practice_test_course',
    ESTIMATED_CONTENT_LENGTH = 'estimated_content_length',
    LAST_UPDATE_DATE = 'last_update_date',
    NUM_CODING_EXERCISES = 'num_coding_exercises',
    CONTEXT_INFO = 'context_info',
    IMAGE_48X27 = 'image_48x27',
    IMAGE_50X50 = 'image_50x50',
    IMAGE_75X75 = 'image_75x75',
    IMAGE_96X54 = 'image_96x54',
    IMAGE_304X171 = 'image_304x171',
    VISIBLE_INSTRUCTORS = 'visible_instructors',
    IS_WISHLISTED = 'is_wishlisted',
    TRACKING_ID = 'tracking_id',
    IS_IN_USER_SUBSCRIPTION = 'is_in_user_subscription',
    IS_GOOGLE_PARTNER_COURSE = 'is_google_partner_course',
}

export enum InstructorFieldSelection {
    ID = 'id',
    TITLE = 'title',
    NAME = 'name',
    DISPLAY_NAME = 'display_name',
    JOB_TITLE = 'job_title',
    IMAGE_50X50 = 'image_50x50',
    IMAGE_100X100 = 'image_100x100',
    IMAGE_75X75 = 'image_75x75',
    IMAGE_125_H = 'image_125_H',
    IMAGE_200_H = 'image_200_H',
    URL = 'url',
    DESCRIPTION = 'description',
    AVG_RATING = 'avg_rating',
    AVG_RATING_RECENT = 'avg_rating_recent',
    TOTAL_NUM_REVIEWS = 'total_num_reviews',
    TOTAL_NUM_STUDENTS = 'total_num_students',
    NUM_PUBLISHED_COURSES = 'num_published_courses',
    NUM_VISIBLE_TAUGHT_COURSES = 'num_visible_taught_courses',
}

/**
 * Ordering options for taught courses
 */
export enum CourseOrdering {
    NEWEST = '-created',
    OLDEST = 'created',
    POPULARITY = '-num_subscribers',
    REVENUE = '-course_performance__revenue_30days',
    RATING = '-avg_rating',
    LANGUAGE_REVENUE = 'lang,-course_performance__revenue_30days',
    RELEVANCE = 'relevance',
}

/**
 * Configuration object for taught courses API requests
 */
export interface TaughtCoursesRequestConfig {
    /** User ID for the API request */
    userId: string;
    /** URL to use as the key in the result maps */
    instructorUrl?: string;
    /** Number of courses to return per page */
    pageSize?: number;
    /** Page number to fetch */
    page?: number;
    /** Context for the request */
    context?: string;
    /** Subcontext for the request */
    subcontext?: string;
    /** Whether to only return organization courses */
    organizationCoursesOnly?: boolean;
    /** Whether to filter HQ courses */
    filterHqCourses?: boolean;
    /** Array of fields to order by */
    ordering?: string[];
    /** Whether to include subscription status for each course (only sent if user is a subscriber) */
    isInUserSubscription?: boolean;
    /** Whether to request a field indicating that courses are Google Partner courses */
    isGooglePartnerCourse?: boolean;
}

/**
 * Instructor representation in the API response
 * Maps to InstructorDetails in our internal types
 * NOTE / HACK: The API does not return an instructor ID, so we use the URL as a unique identifier.
 */
export interface ApiInstructor {
    _class: string;
    title: string;
    name: string;
    display_name: string;
    job_title: string;
    image_50x50: string;
    image_100x100: string;
    url?: string;
    image_75x75?: string;
    image_125_H?: string;
    image_200_H?: string;
    description?: string;
    avg_rating?: number;
    avg_rating_recent?: number;
    total_num_students?: number;
    total_num_reviews?: number;
    num_published_courses?: number;
    num_visible_taught_courses?: number;
}

/**
 * Locale representation in the API response
 */
export interface ApiLocale {
    _class: string;
    locale: string;
    title: string;
    english_title: string;
    simple_english_title?: string;
}

/**
 * Label representation in the API response
 */
export interface ApiLabel {
    id: number;
    display_name: string;
    title: string;
    topic_channel_url: string;
    url: string;
    tracking_object_type: string;
}

/**
 * Category representation in the API response
 */
export interface ApiCategory {
    id: number;
    title: string;
    url: string;
    tracking_object_type: string;
}

/**
 * Context info representation in the API response
 */
export interface ApiContextInfo {
    category: ApiCategory;
    subcategory: ApiCategory | null;
    label?: ApiLabel;
}

/**
 * API Response for a single Course item
 * Structured to show mappings to our internal Course/BrowseCourse types
 */
export interface CourseItemResponse {
    _class: string;
    id: number;
    title: string;
    url: string;
    headline: string;
    image_48x27: string;
    image_50x50: string;
    image_75x75: string;
    image_96x54: string;
    image_100x100: string;
    image_304x171: string;
    instructional_level_simple: string;
    avg_rating: number;
    num_reviews: number;
    num_published_lectures: number;
    caption_languages: string[];
    locale: ApiLocale;
    published_title: string;
    is_practice_test_course: boolean;
    estimated_content_length: number;
    last_update_date: string;
    num_coding_exercises: number;
    context_info: ApiContextInfo;
    tracking_id?: string;
    visible_instructors?: ApiInstructor[];
    is_in_user_subscription?: boolean;
    is_google_partner_course?: boolean;
}

/**
 * API Response structure for the Taught Courses API
 * Follows standard pagination pattern
 */
export interface TaughtCoursesApiResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: CourseItemResponse[];
}
